import { useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import Header from "../components/Header";
import StarRating from "../components/StarRating";
import { useRecipe } from "../hooks/useRecipe";
import { useAuth } from "../context/AuthContext";
import { useDeleteRecipe } from "../hooks/useDeleteRecipe";

const IMAGES_BASE =
  import.meta.env.VITE_IMAGES_BASE_URL ?? "http://localhost:3001/images";

export default function RecipeDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const { data: recipe, isLoading, error } = useRecipe(slug ?? "");
  const { user } = useAuth();
  const { mutateAsync: deleteRecipe, isPending: deleting } = useDeleteRecipe();
  const navigate = useNavigate();
  const [confirmDelete, setConfirmDelete] = useState(false);

  async function handleDelete() {
    if (!recipe) return;
    await deleteRecipe(recipe.recipeId);
    navigate("/");
  }

  if (isLoading) {
    return (
      <>
        <Header />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 animate-pulse space-y-4">
          <div className="h-8 bg-stone-200 rounded w-1/2" />
          <div className="h-96 bg-stone-200 rounded-2xl" />
          <div className="h-4 bg-stone-200 rounded w-3/4" />
          <div className="h-4 bg-stone-200 rounded w-1/2" />
        </div>
      </>
    );
  }

  if (error || !recipe) {
    return (
      <>
        <Header />
        <div className="max-w-4xl mx-auto px-4 py-20 text-center text-stone-500 space-y-4">
          <p className="text-5xl">🔍</p>
          <h2 className="text-xl font-semibold">Recipe not found</h2>
          <Link to="/" className="text-herb underline text-sm">
            Back to recipes
          </Link>
        </div>
      </>
    );
  }

  const heroUrl = `${IMAGES_BASE}/${recipe.imageKey}`;

  return (
    <>
      <Header />
      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        {/* Hero image */}
        <div className="rounded-2xl overflow-hidden h-[400px] bg-stone-100">
          <img
            src={heroUrl}
            alt={recipe.title}
            className="w-full h-full object-cover"
          />
        </div>

        {/* Title, tags, rating */}
        <div className="space-y-3">
          <div className="flex items-start justify-between gap-4">
            <h1
              className="text-3xl font-bold text-stone-900"
              style={{ fontFamily: "Georgia, serif" }}
            >
              {recipe.title}
            </h1>

            {user && (
              <div className="flex gap-2 flex-shrink-0">
                <Link
                  to={`/recipe/${recipe.slug}/edit`}
                  className="text-sm px-4 py-2 rounded-full border border-stone-300 text-stone-600 hover:border-herb hover:text-herb transition-colors"
                >
                  Edit
                </Link>
                {confirmDelete ? (
                  <div className="flex gap-2">
                    <button
                      onClick={handleDelete}
                      disabled={deleting}
                      className="text-sm px-4 py-2 rounded-full bg-red-500 text-white hover:bg-red-600 disabled:opacity-50 transition-colors"
                    >
                      {deleting ? "Deleting…" : "Confirm delete"}
                    </button>
                    <button
                      onClick={() => setConfirmDelete(false)}
                      className="text-sm px-4 py-2 rounded-full border border-stone-300 text-stone-600 hover:border-stone-400 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setConfirmDelete(true)}
                    className="text-sm px-4 py-2 rounded-full border border-stone-300 text-stone-600 hover:border-red-400 hover:text-red-500 transition-colors"
                  >
                    Delete
                  </button>
                )}
              </div>
            )}
          </div>

          <div className="flex flex-wrap gap-2">
            {recipe.tags.map((tag) => (
              <span
                key={tag}
                className="text-xs bg-stone-100 text-stone-600 px-2.5 py-1 rounded-full"
              >
                {tag}
              </span>
            ))}
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            <StarRating rating={recipe.rating} />
            <span className="text-sm text-stone-500">
              {recipe.cuisine} &bull; {recipe.course}
            </span>
            {recipe.isFavorite && (
              <span className="text-amber-500 text-sm font-medium">
                ★ Favorite
              </span>
            )}
          </div>

          <p className="text-stone-600 leading-relaxed">{recipe.description}</p>
        </div>

        {/* Metadata bar */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 bg-stone-50 rounded-xl p-4">
          {[
            { label: "Prep", value: `${recipe.prepTimeMinutes} min` },
            { label: "Cook", value: `${recipe.cookTimeMinutes} min` },
            { label: "Total", value: `${recipe.totalTimeMinutes} min` },
            { label: "Servings", value: String(recipe.servings) },
          ].map(({ label, value }) => (
            <div key={label} className="text-center">
              <p className="text-xs text-stone-500 uppercase tracking-wide">
                {label}
              </p>
              <p className="text-lg font-semibold text-stone-800">{value}</p>
            </div>
          ))}
        </div>

        {/* Ingredients */}
        <section>
          <h2
            className="text-xl font-semibold mb-4 text-stone-800"
            style={{ fontFamily: "Georgia, serif" }}
          >
            Ingredients
          </h2>
          {recipe.ingredients.map((group, gi) => (
            <div key={gi} className="mb-5">
              {group.group && (
                <h3 className="text-sm font-semibold uppercase tracking-wide text-stone-500 mb-2">
                  {group.group}
                </h3>
              )}
              <ul className="space-y-1.5">
                {group.items.map((item, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-2 text-stone-700 text-sm"
                  >
                    <span className="mt-2 w-1.5 h-1.5 rounded-full bg-herb flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </section>

        {/* Steps */}
        <section>
          <h2
            className="text-xl font-semibold mb-4 text-stone-800"
            style={{ fontFamily: "Georgia, serif" }}
          >
            Instructions
          </h2>
          <ol className="space-y-6">
            {recipe.steps.map((step) => (
              <li key={step.stepNumber} className="flex gap-4">
                <span className="flex-shrink-0 w-8 h-8 rounded-full bg-herb text-white text-sm font-bold flex items-center justify-center">
                  {step.stepNumber}
                </span>
                <p className="text-stone-700 leading-relaxed pt-1">
                  {step.instruction}
                </p>
              </li>
            ))}
          </ol>
        </section>

        {/* Notes */}
        {recipe.notes && (
          <section className="bg-amber-50 border border-amber-200 rounded-xl p-4">
            <h2 className="text-sm font-semibold text-amber-800 mb-2 uppercase tracking-wide">
              Notes
            </h2>
            <p className="text-amber-900 text-sm leading-relaxed">
              {recipe.notes}
            </p>
          </section>
        )}

        {/* Source */}
        {recipe.source && (
          <p className="text-xs text-stone-400 pb-8">
            Source:{" "}
            {recipe.sourceUrl ? (
              <a
                href={recipe.sourceUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-stone-600"
              >
                {recipe.source}
              </a>
            ) : (
              recipe.source
            )}
          </p>
        )}
      </article>
    </>
  );
}
